import { useLayoutEffect, useRef } from "react";

// useCssVars sets CSS custom properties on the returned ref's element after
// every render. The point is to keep dynamic values (a width percentage,
// a colour from data) OUT of the rendered HTML's `style="..."` attribute,
// so the page's CSP can be `style-src 'self'` with no `'unsafe-inline'`.
//
// Why a ref + useLayoutEffect rather than `style={{...}}`:
//   - React's `style` prop emits a `style="..."` attribute on the element,
//     which CSP `style-src 'self'` blocks without `'unsafe-inline'`.
//   - element.style.setProperty(...) is a CSSOM mutation from JavaScript
//     that's already script-src allowed; modern browsers don't apply
//     style-src to it. Result: the rendered HTML has NO style attribute,
//     and the dynamic value is applied via CSSOM after mount.
//
// Pair the ref with a Tailwind arbitrary-value class that consumes the
// variable, e.g. `[width:var(--w)]`, so the variable has a place to land.
export function useCssVars<T extends HTMLElement = HTMLElement>(vars: Record<string, string | number | null | undefined>) {
  const ref = useRef<T | null>(null);
  // Stable serialisation so React re-runs the effect only when a value changes.
  const key = JSON.stringify(vars);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    for (const [name, val] of Object.entries(vars)) {
      if (val == null) {
        el.style.removeProperty(name);
      } else {
        el.style.setProperty(name, String(val));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return ref;
}
